import { Component } from '@theme/component';

/**
 * Collection coverflow (sections/collection-coverflow.liquid).
 *
 * Positions every card from the active index: the active card centred at full size, its
 * neighbours offset and scaled back with a cream veil, and anything further hidden. Offsets
 * follow the design (300/520px desktop, 170/280px mobile). All motion is a CSS transition on
 * the custom properties set here.
 *
 * A click on a side card brings it to the centre instead of following the link; only the
 * centred card navigates.
 *
 * @typedef {{ stage: HTMLElement, cards: HTMLAnchorElement[], chips?: HTMLButtonElement[], count?: HTMLElement }} CoverflowRefs
 * @extends {Component<CoverflowRefs>}
 */
class CollectionCoverflowComponent extends Component {
  requiredRefs = ['stage'];

  #index = 0;

  #mobile = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.cards?.length) return;
    this.setAttribute('data-ready', '');
    this.#mobile.addEventListener('change', this.#render);
    this.addEventListener('keydown', this.#onKeydown);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mobile.removeEventListener('change', this.#render);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @param {number} index */
  goTo(index) {
    const count = this.refs.cards?.length ?? 0;
    if (!count) return;
    this.#index = ((Number(index) % count) + count) % count;
    this.#render();
  }

  prev() {
    this.goTo(this.#index - 1);
  }

  next() {
    this.goTo(this.#index + 1);
  }

  /** @param {MouseEvent} event */
  onCardClick(event) {
    const card = /** @type {HTMLElement} */ (event.target).closest('.coverflow__card');
    if (!(card instanceof HTMLElement)) return;
    const index = Number(card.dataset.index);
    if (index !== this.#index) {
      event.preventDefault();
      this.goTo(index);
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'ArrowRight') this.next();
  };

  #render = () => {
    const cards = this.refs.cards ?? [];
    const count = cards.length;
    const offsets = this.#mobile.matches ? [0, 170, 280] : [0, 300, 520];
    const scales = [1, 0.82, 0.66];
    const veils = [0, 0.25, 0.45];

    cards.forEach((card, i) => {
      let d = i - this.#index;
      if (d > count / 2) d -= count;
      if (d < -count / 2) d += count;
      const ad = Math.abs(d);
      const step = Math.min(ad, 2);
      const hidden = ad > 2;

      card.style.setProperty('--x', `${d === 0 ? 0 : Math.sign(d) * (offsets[step] ?? 0)}px`);
      card.style.setProperty('--s', String(hidden ? 0.5 : scales[step]));
      card.style.setProperty('--z', String(10 - ad));
      card.style.setProperty('--o', hidden ? '0' : '1');
      card.style.setProperty('--veil', String(hidden ? 0.6 : veils[step]));
      card.style.setProperty('--shadow', d === 0 ? 'var(--bk-shadow-lift)' : 'none');
      card.toggleAttribute('data-active', d === 0);
      card.toggleAttribute('data-hidden', hidden);
      card.tabIndex = d === 0 ? 0 : -1;
      card.setAttribute('aria-hidden', String(d !== 0));
    });

    this.refs.chips?.forEach((chip, i) => chip.setAttribute('aria-pressed', String(i === this.#index)));

    if (this.refs.count) {
      const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
      this.refs.count.textContent = `${pad(this.#index + 1)} / ${pad(count)}`;
    }
  };
}

if (!customElements.get('collection-coverflow-component')) {
  customElements.define('collection-coverflow-component', CollectionCoverflowComponent);
}
